import { tinyDeskShowcaseClips } from "@marketing/tiny-desk/config/tiny-desk-showcase";
import { transmission5DanceShowcaseClips } from "@marketing/transmission-5-dance/config/transmission-5-dance-showcase";
import { byeByeTrendShowcaseClips } from "@marketing/bye-bye-trend/config/bye-bye-trend-showcase";
import { hopHopHopDanceShowcaseClips } from "@marketing/hop-hop-hop-dance/config/hop-hop-hop-dance-showcase";
import { aiFrenchKissShowcaseClips } from "@marketing/ai-french-kiss/config/ai-french-kiss-showcase";
import { flyingCarShowcaseClips } from "@marketing/flying-car/config/flying-car-showcase";
import { teslaRoadsterShowcaseClips } from "@marketing/tesla-roadster/config/tesla-roadster-showcase";
import { aiKissShowcaseClips } from "@marketing/ai-kiss/config/ai-kiss-showcase";
import { aiAirKissShowcaseClips } from "@marketing/ai-air-kiss/config/ai-air-kiss-showcase";
import { aiTongueKissShowcaseClips } from "@marketing/ai-tongue-kiss/config/ai-tongue-kiss-showcase";
import { aiDanceVideoShowcaseClips } from "@marketing/ai-dance-video/config/ai-dance-video-showcase";
import { aiHugShowcaseClips } from "@marketing/ai-hug/config/ai-hug-showcase";
import { aiWeddingShowcaseClips } from "@marketing/ai-wedding/config/ai-wedding-showcase";
import { bulin47ShowcaseClips } from "@marketing/bulin-47/config/bulin-47-showcase";
import { burningBridgesShowcaseClips } from "@marketing/burning-bridges/config/burning-bridges-showcase";
import { chuttamalleShowcaseClips } from "@marketing/chuttamalle/config/chuttamalle-showcase";
import { hotelLobbyShowcaseClips } from "@marketing/hotel-lobby/config/hotel-lobby-showcase";
import { ozilGoalShowcaseClips } from "@marketing/ozil-goal/config/ozil-goal-showcase";
import { rumpelstiltskinShowcaseClips } from "@marketing/rumpelstiltskin/config/rumpelstiltskin-showcase";
import { zombieTrendShowcaseClips } from "@marketing/zombie-trend/config/zombie-trend-showcase";
import { buildR2AssetsImageUrl } from "@saas/shared/lib/image-url-builder";
import { ClapperboardIcon } from "lucide-react";
import { type SiteNavItem, VIDEO_EFFECT_ITEMS } from "./site-nav";

export type VideoTrendLink = SiteNavItem & {
	poster: string;
	/** 特效页互推卡下面那句目标页描述的 i18n key；热梗页的卡不带描述 */
	descriptionKey?: string;
};

/** 封面取各页样片区第一条的 poster */
const POSTERS: Record<string, string | undefined> = {
	"/tiny-desk-ai-video-generator": tinyDeskShowcaseClips[0]?.poster,
	"/transmission-5-dance-ai-video-generator":
		transmission5DanceShowcaseClips[0]?.poster,
	"/bye-bye-trend-ai-video-generator": byeByeTrendShowcaseClips[0]?.poster,
	"/hop-hop-hop-dance-ai-video-generator":
		hopHopHopDanceShowcaseClips[0]?.poster,
	"/flying-car-ai": flyingCarShowcaseClips[0]?.poster,
	"/tesla-roadster-ai-video-generator": teslaRoadsterShowcaseClips[0]?.poster,
	"/hotel-lobby-ai-video-generator": hotelLobbyShowcaseClips[0]?.poster,
	"/chuttamalle-ai-video-generator": chuttamalleShowcaseClips[0]?.poster,
	"/zombie-trend-ai-video-generator": zombieTrendShowcaseClips[0]?.poster,
	"/rumpelstiltskin-ai-video-generator":
		rumpelstiltskinShowcaseClips[0]?.poster,
	"/burning-bridges-ai-video-generator":
		burningBridgesShowcaseClips[0]?.poster,
	"/bulin-47-ai-video-generator": bulin47ShowcaseClips[0]?.poster,
	"/ozil-goal-ai-video-generator": ozilGoalShowcaseClips[0]?.poster,
	"/ai-kiss-video-generator": aiKissShowcaseClips[0]?.poster,
	"/ai-french-kiss-video-generator": aiFrenchKissShowcaseClips[0]?.poster,
	"/ai-wedding-video-generator": aiWeddingShowcaseClips[0]?.poster,
	"/ai-hug-video-generator": aiHugShowcaseClips[0]?.poster,
	"/ai-air-kiss-video-generator": aiAirKissShowcaseClips[0]?.poster,
	"/ai-tongue-kiss-generator": aiTongueKissShowcaseClips[0]?.poster,
	"/ai-dance-video-generator": aiDanceVideoShowcaseClips[0]?.poster,
};

/** 特效页互推卡上每个目标页的一句描述，文案在 home.video.effects.cards */
const DESCRIPTION_KEYS: Record<string, string> = {
	"/tiny-desk-ai-video-generator": "home.video.effects.cards.tinyDesk",
	"/transmission-5-dance-ai-video-generator":
		"home.video.effects.cards.transmission5Dance",
	"/bye-bye-trend-ai-video-generator": "home.video.effects.cards.byeByeTrend",
	"/hop-hop-hop-dance-ai-video-generator":
		"home.video.effects.cards.hopHopHopDance",
	"/flying-car-ai": "home.video.effects.cards.flyingCar",
	"/tesla-roadster-ai-video-generator":
		"home.video.effects.cards.teslaRoadster",
	"/hotel-lobby-ai-video-generator": "home.video.effects.cards.hotelLobby",
	"/chuttamalle-ai-video-generator": "home.video.effects.cards.chuttamalle",
	"/zombie-trend-ai-video-generator": "home.video.effects.cards.zombieTrend",
	"/rumpelstiltskin-ai-video-generator":
		"home.video.effects.cards.rumpelstiltskin",
	"/burning-bridges-ai-video-generator":
		"home.video.effects.cards.burningBridges",
	"/bulin-47-ai-video-generator": "home.video.effects.cards.bulin47",
	"/ozil-goal-ai-video-generator": "home.video.effects.cards.ozilGoal",
	"/ai-kiss-video-generator": "home.video.effects.cards.aiKiss",
	"/ai-french-kiss-video-generator": "home.video.effects.cards.aiFrenchKiss",
	"/ai-wedding-video-generator": "home.video.effects.cards.aiWedding",
	"/ai-hug-video-generator": "home.video.effects.cards.aiHug",
	"/ai-air-kiss-video-generator": "home.video.effects.cards.aiAirKiss",
	"/ai-tongue-kiss-generator": "home.video.effects.cards.aiTongueKiss",
	"/ai-dance-video-generator": "home.video.effects.cards.aiDanceVideo",
};

/**
 * 页脚 AI Video Tools 列里哪些是常青的特效页（kiss / hug 这类），其余是热梗页。
 * 两类的互推规矩不同（2026-10-12 定，依据 docs/research/imgveo-cross-links-2026-10-12.md）
 */
const EFFECT_PAGE_HREFS = new Set([
	"/ai-kiss-video-generator",
	"/ai-wedding-video-generator",
	"/ai-hug-video-generator",
	"/ai-air-kiss-video-generator",
	"/ai-tongue-kiss-generator",
	"/ai-dance-video-generator",
]);

/**
 * 页脚 AI Video Tools 列里有封面的页面；标题和链接跟页脚同源，新页进了 VIDEO_EFFECT_ITEMS
 * 再补一张封面就会出现
 */
const VIDEO_LINKS: VideoTrendLink[] = VIDEO_EFFECT_ITEMS.flatMap((item) => {
	const poster = POSTERS[item.href];
	return poster ? [{ ...item, poster }] : [];
});

/**
 * 特效页第 4 张固定的兜底卡：通用图生视频页。封面是首页样片 cloud-ice-cream 的 poster
 * （没有文字、和接吻不打架）；直接拼地址，不把首页整份样片配置拖进客户端包
 */
const IMAGE_TO_VIDEO_FALLBACK: VideoTrendLink = {
	titleKey: "imageToVideo.title",
	href: "/image-to-video",
	icon: ClapperboardIcon,
	poster: buildR2AssetsImageUrl(
		"home/showcase/seedance/posters/cloud-ice-cream.jpg",
	),
	descriptionKey: "home.video.effects.cards.imageToVideo",
};

export function isVideoEffectPage(pathname: string): boolean {
	return EFFECT_PAGE_HREFS.has(pathname);
}

/**
 * 当前页该推哪几张卡：
 * - 热梗页（2026-10-10 老公定）：推另外几个热梗页，全推、不带描述；
 * - 特效页（2026-10-12 定）：同页型优先、新上线的在前，不够再拿热梗页补，最多 3 张，
 *   第 4 张固定 /image-to-video 兜底，每张带一句描述
 */
export function getVideoLinkCards(pathname: string): VideoTrendLink[] {
	const others = VIDEO_LINKS.filter((item) => item.href !== pathname);
	if (!isVideoEffectPage(pathname)) {
		const trends = others.filter(
			(item) => !EFFECT_PAGE_HREFS.has(item.href),
		);
		// 新页按方案展示最近四个热梗。
		return pathname === "/hop-hop-hop-dance-ai-video-generator" ||
			pathname === "/bye-bye-trend-ai-video-generator" ||
			pathname === "/transmission-5-dance-ai-video-generator" ||
			pathname === "/tiny-desk-ai-video-generator" ||
			pathname === "/flying-car-ai" ||
			pathname === "/ai-french-kiss-video-generator"
			? trends
					.slice(-4)
					.reverse()
					.map((item) => ({
						...item,
						descriptionKey: DESCRIPTION_KEYS[item.href],
					}))
			: trends;
	}
	// VIDEO_EFFECT_ITEMS 按上线顺序登记，倒过来就是新的在前
	const newestFirst = [...others].reverse();
	return [
		...newestFirst.filter((item) => EFFECT_PAGE_HREFS.has(item.href)),
		...newestFirst.filter((item) => !EFFECT_PAGE_HREFS.has(item.href)),
	]
		.slice(0, 3)
		.map(
			(item): VideoTrendLink => ({
				...item,
				descriptionKey: DESCRIPTION_KEYS[item.href],
			}),
		)
		.concat(IMAGE_TO_VIDEO_FALLBACK);
}
